/**
 * 메인탭 오늘 카드의 「우리 기록」 요약.
 *
 * 오늘 카드는 지금까지 장소·지도·멤버까지만 보여 줬습니다. 그 아래에
 * 멤버들이 남긴 사진과 글을 얹습니다.
 *
 * 목록 API 를 부르지 않습니다 — 카드 하나에 스무 건을 받아 두 줄만 그리는 건
 * 낭비라, 서버에 요약 전용 경로를 뒀습니다(GET /meetings/{id}/journal/summary).
 *
 * 기록이 하나도 없으면 사진 줄과 최근 글은 숨기고 작성기만 남깁니다.
 * 첫 글을 남기는 자리가 사라지면 아무 일도 일어나지 않습니다.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PhotoViewerModal, SectionHeader } from 'components/ds';
import { IcChevronRight } from 'components/common/icons/EmojiIcons';
import { MeetingJournalSection } from './MeetingJournalSection';
import type { JournalAnchor } from './JournalSheet';
import type { JournalSummary } from 'hooks/useMeetingJournal';
import styles from './JournalSummaryCard.module.css';

/** 사진 줄에 놓을 칸 수 — 마지막 칸에 남은 장수를 겹칩니다 */
const SLOTS = 4;

interface Props {
    meetingId: string;
    summary: JournalSummary | null;
    anchor: JournalAnchor | null;
    nickname?: string | null;
    profileUrl?: string | null;
    onPosted?: () => void;
}

export const JournalSummaryCard: React.FC<Props> = ({
    meetingId, summary, anchor, nickname, profileUrl, onPosted,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    /** 크게 보기로 연 장수. null 이면 닫힘 */
    const [viewer, setViewer] = useState<number | null>(null);

    const photos = (summary?.photos || []).slice(0, SLOTS);
    const rest = Math.max(0, (summary?.photoCount || 0) - photos.length);
    const latest = summary?.latest;
    /* 사진만 올라온 경우(후기 행 없는 사진)도 '있음' 입니다 —
       사진 줄은 보이는데 부제만 비어 있다고 말하면 어긋납니다. */
    const hasAny = (summary?.entryCount || 0) > 0 || (summary?.photoCount || 0) > 0;

    const openFeed = () => {
        const q = new URLSearchParams();
        if (anchor?.placeId) q.set('placeId', anchor.placeId);
        if (anchor?.placeName) q.set('placeName', anchor.placeName);
        if (anchor?.dayNumber) q.set('day', String(anchor.dayNumber));
        if (anchor?.subId) q.set('subId', String(anchor.subId));
        navigate(`/meeting/${meetingId}/journal?${q.toString()}`);
    };

    return (
        <div className={styles.wrap}>
            <SectionHeader
                title={t('journal.sectionTitle')}
                sub={hasAny
                    ? t('journal.sectionSub', {
                        photos: summary?.photoCount || 0,
                        entries: summary?.entryCount || 0,
                    })
                    : t('journal.empty')}
            />

            {photos.length > 0 && (
                <>
                    {/* 사진을 누르면 그 사진을 크게 봅니다. 예전에는 줄 전체가 하나의
                        버튼이라 어느 사진을 눌러도 목록으로 갔고, 크게 볼 방법이
                        없었습니다. 목록은 아래 버튼으로 갑니다. */}
                    <div className={styles.strip}>
                        {photos.map((url, i) => (
                            <button
                                key={url}
                                type="button"
                                className={styles.cell}
                                onClick={() => setViewer(i)}
                            >
                                <img src={url} alt="" loading="lazy" />
                                {/* 칸을 늘리면 한 칸이 좁아져 무엇인지 안 보입니다.
                                   마지막 사진 위에 남은 장수를 겹쳐 칸 수를 지킵니다. */}
                                {i === photos.length - 1 && rest > 0 && (
                                    <span className={styles.more}>+{rest}</span>
                                )}
                            </button>
                        ))}
                    </div>
                    <button type="button" className={styles.viewAll} onClick={openFeed}>
                        {t('common.viewAll')}
                        <IcChevronRight size={14} />
                    </button>
                </>
            )}

            {latest && (
                <button type="button" className={styles.latest} onClick={openFeed}>
                    <span className={styles.latestText}>
                        <b>{latest.nickname || ''}</b>
                        {latest.placeName ? ` · ${latest.placeName}` : ''}
                        {latest.content ? ' — ' : ''}
                        <span className={styles.excerpt}>{latest.content || ''}</span>
                    </span>
                    <IcChevronRight size={16} />
                </button>
            )}

            {/* 크게 보기 — 좌우로 넘겨 봅니다. 공유도 여기서 합니다. */}
            <PhotoViewerModal
                open={viewer !== null}
                photos={photos}
                index={viewer ?? 0}
                shareTitle={t('journal.sectionTitle')}
                onClose={() => setViewer(null)}
            />

            <div className={styles.composer}>
                <MeetingJournalSection
                    meetingId={meetingId}
                    anchor={anchor}
                    nickname={nickname}
                    profileUrl={profileUrl}
                    onPosted={onPosted}
                />
            </div>
        </div>
    );
};
